import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useOptionalInstance } from '../../contexts/InstanceContext';
import { buildMenuItems } from '../../utils/participantEntry';

const Container = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
  min-width: 280px;
`;

const Section = styled.div`
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #0a0a0a;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: #252525;
  }

  ${props => props.$isSelected && css`
    background: rgba(0, 153, 255, 0.15);

    &:hover {
      background: rgba(0, 153, 255, 0.2);
    }
  `}

  ${props => props.$isDisabled && css`
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  `}
`;

const OptionIcon = styled.span`
  font-size: 18px;
  width: 24px;
  text-align: center;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const OptionDescription = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 2px;
`;

const SelectedIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0099ff;
`;

const LoadingSpinner = styled.div`
  padding: 24px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

// Icons per module family. The workshop views are the chain's modules that have
// a wall view, in chain order — the same list as the participant menu (#685).
const FAMILY_ICONS = {
  impact_path: '🔗',
  systemic_map: '🕸️',
  pain_points_collect: '🩹',
  pivot_points: '🎯',
  lenses: '🔍',
  solutions: '💡',
  report: '📄',
};

// Wall basics that aren't workshop content but every setup needs
const GENERAL_VIEWS = [
  { id: 'kuutio', icon: '🎲', title: 'Kuutio', description: 'Pyörivä kuutio' },
  { id: 'welcome', icon: '👋', title: 'Welcome', description: 'Tervetuloa-näkymä' },
  { id: 'blank', icon: '⬛', title: 'Blank', description: 'Tyhjä näyttö' },
];

/**
 * ScreenContentSelector - Dropdown for selecting screen content
 *
 * @param {string} currentView - Currently selected view ID
 * @param {function} onSelect - Callback when view is selected
 * @param {array} presentations - List of uploaded presentations
 * @param {boolean} showPresentations - Whether to show presentation section
 * @param {boolean} isLoading - Show loading state
 * @param {array} disabledViews - List of view IDs that are disabled
 */
const ScreenContentSelector = ({
  currentView,
  onSelect,
  presentations = [],
  showPresentations = true,
  isLoading = false,
  disabledViews = [],
}) => {
  const { t, language } = useTranslation();
  const participantChain = useOptionalInstance()?.participantChain;

  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner>Loading...</LoadingSpinner>
      </Container>
    );
  }

  const toView = (item) => ({
    id: item.wallView,
    icon: FAMILY_ICONS[item.family] || '📺',
    title: item.label,
    description: null,
  });
  const walled = buildMenuItems(participantChain, language).filter((item) => item.wallView);
  const groups = [
    { key: 'workshop', views: walled.filter((item) => !item.isReport).map(toView) },
    { key: 'results', label: t('navigation.results'), views: walled.filter((item) => item.isReport).map(toView) },
    { key: 'general', label: 'Yleiset', views: GENERAL_VIEWS },
  ].filter((group) => group.views.length);

  const renderOption = (view) => (
    <Option
      key={view.id}
      $isSelected={currentView === view.id}
      $isDisabled={disabledViews.includes(view.id)}
      onClick={() => !disabledViews.includes(view.id) && onSelect?.(view.id)}
    >
      <OptionIcon>{view.icon}</OptionIcon>
      <OptionContent>
        <OptionLabel>{view.title}</OptionLabel>
        {view.description && <OptionDescription>{view.description}</OptionDescription>}
      </OptionContent>
      {currentView === view.id && <SelectedIndicator />}
    </Option>
  );

  return (
    <Container>
      {groups.map(group => (
        <Section key={group.key || group.label}>
          {(group.key === 'results' || group.key === 'general') && (
            <SectionHeader>{group.label}</SectionHeader>
          )}
          <OptionList>{group.views.map(renderOption)}</OptionList>
        </Section>
      ))}
    </Container>
  );
};

export default ScreenContentSelector;
